import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { calculateVolume, getCorrectedLitreWeight } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getCargoTemperature, getCorrectedVoyageTemperatureRange } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { useStowageStore } from 'src/context/StowageContext';
import { getChartererName, getFormattedCargoNumber, getRange, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { CargoTankDetails } from 'src/types/ViewModel/Stowage/CargoTankDetails';
import { CargoDetailsTable, PanelSectionContent } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import Decimal from 'decimal.js';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';

interface TankCargoDetailsVisualProps {
    cargoTankDetails: CargoTankDetails;
}

const TankCargoDetailsPanel: React.FC<TankCargoDetailsVisualProps> = ({ cargoTankDetails }) => {
    const { activeStowagePlan, setIsCargoDetailsPanelOpen, mouseClickPosition } = useStowageStore();

    // Define panel dimensions for position calculation
    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.MEDIUM_WIDTH,
        height: 500, // Estimate the panel height
    };

    // Use the hook to calculate optimal position
    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    const onClosePanel = () => {
        setIsCargoDetailsPanelOpen(false);
    };

    return (
        cargoTankDetails &&
        cargoTankDetails.cargoContent && (
            <OdfjellPanel
                headerTitle={
                    'Cargo #' +
                    getFormattedCargoNumber(
                        cargoTankDetails.cargoContent.cargo.voyageNumber,
                        cargoTankDetails.cargoContent.cargo.cargoNumber,
                        isManualCargo(cargoTankDetails.cargoContent.cargo)
                    )
                }
                defaultPosition="center"
                initialPosition={mouseClickPosition ? panelPosition : undefined}
                panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
                actionButtonText="Close"
                onAction={onClosePanel}
                onCancel={onClosePanel}
            >
                <PanelSectionContent>
                    <CargoDetailsTable>
                        <tbody>
                            <tr>
                                <td>Trade name</td>
                                <td>
                                    <a
                                        href={'https://commoditybook.odfjell.com/#/product/' + cargoTankDetails.cargoContent.cargo.productId}
                                        target="_blank"
                                        title="Click here to open the Commodity Book"
                                        rel="noreferrer"
                                    >
                                        {cargoTankDetails.cargoContent.cargo.product.tradeName}{' '}
                                        <FontAwesomeIcon className="external-link-icon" icon="external-link-alt" />
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <td>Product name</td>
                                <td>{cargoTankDetails.cargoContent.cargo.product.category.name}</td>
                            </tr>
                            <tr>
                                <td>Charterer</td>
                                <td>{getChartererName(cargoTankDetails.cargoContent.cargo)}</td>
                            </tr>
                            <tr>
                                <td title={cargoTankDetails.cargoContent.cargo.loadingPortNames.map((o) => o.name).join(', ')}>Loading ports</td>
                                <td>{cargoTankDetails.cargoContent.cargo.loadingPortNames.map((o) => o.name).join(', ')}</td>
                            </tr>
                            <tr>
                                <td title={cargoTankDetails.cargoContent.cargo.unloadingPortNames.map((o) => o.name).join(', ')}>Unloading ports</td>
                                <td>{cargoTankDetails.cargoContent.cargo.unloadingPortNames.map((o) => o.name).join(', ')}</td>
                            </tr>
                            <tr>
                                <td>Nominated weight</td>
                                <td>{roundTo3Decimals(cargoTankDetails.cargoContent.weight.toNumber()) ?? 'N/A'} MT</td>
                            </tr>
                            <tr>
                                <td>Nominated volume</td>
                                <td>
                                    {roundTo3Decimals(
                                        calculateVolume(
                                            cargoTankDetails.cargoContent.weight,
                                            cargoTankDetails.cargoContent.cargo,
                                            getCargoTemperature(activeStowagePlan, cargoTankDetails.cargoContent.cargo, cargoTankDetails.tank)
                                        ).toNumber()
                                    ) ?? 'N/A'}{' '}
                                    m³
                                </td>
                            </tr>
                            <tr>
                                <td>Ship figure weight</td>
                                <td>
                                    <span className={cargoTankDetails.cargoContent.stowage?.isShipFigureAdjusted ? 'adjusted-ship-figure' : ''}>
                                        {roundTo3Decimals(cargoTankDetails.cargoContent.stowage?.shipFigure) ?? 'N/A'} MT
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td>Ship figure volume</td>
                                <td>
                                    <span className={cargoTankDetails.cargoContent.stowage?.isShipFigureAdjusted ? 'adjusted-ship-figure' : ''}>
                                        {cargoTankDetails.cargoContent.stowage?.shipFigure
                                            ? roundTo3Decimals(
                                                  calculateVolume(
                                                      new Decimal(cargoTankDetails.cargoContent.stowage.shipFigure),
                                                      cargoTankDetails.cargoContent.cargo,
                                                      getCargoTemperature(
                                                          activeStowagePlan,
                                                          cargoTankDetails.cargoContent.cargo,
                                                          cargoTankDetails.tank
                                                      )
                                                  ).toNumber()
                                              )
                                            : 'N/A'}{' '}
                                        m³
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td>USCG</td>
                                <td>
                                    {cargoTankDetails.cargoContent.cargo.product.uscgGroup.number}{' '}
                                    {cargoTankDetails.cargoContent.cargo.product.uscgGroup.name}
                                </td>
                            </tr>
                            <tr>
                                <td>Heating</td>
                                <td>
                                    {getCorrectedVoyageTemperatureRange(cargoTankDetails.cargoContent.cargo) ?? 'N/A'}
                                    &deg;C
                                </td>
                            </tr>
                            <tr>
                                <td>Cargo temperature</td>
                                <td>
                                    {getCargoTemperature(activeStowagePlan, cargoTankDetails.cargoContent.cargo, cargoTankDetails.tank)}
                                    &deg;C
                                </td>
                            </tr>
                            <tr>
                                <td>Litre weight</td>
                                <td>
                                    {roundTo3Decimals(
                                        getCorrectedLitreWeight(
                                            getCargoTemperature(activeStowagePlan, cargoTankDetails.cargoContent.cargo, cargoTankDetails.tank),
                                            cargoTankDetails.cargoContent.cargo
                                        )?.toNumber() ?? 0
                                    ) || 'N/A'}{' '}
                                    kg/l
                                </td>
                            </tr>
                            <tr>
                                <td>Max adjacent temperature</td>
                                <td>
                                    {cargoTankDetails.cargoContent.cargo.productChanges?.maxAdjacentTemperature ??
                                        cargoTankDetails.cargoContent.cargo.product.maxAdjacentTemperature}
                                    &deg;C
                                </td>
                            </tr>
                            {cargoTankDetails.cargoContent.cargo.product.category.ibcSpecificRequirements?.length > 0 && (
                                <tr>
                                    <td>IBC specific requirements</td>
                                    <td>
                                        {cargoTankDetails.cargoContent.cargo.product.category.ibcSpecificRequirements
                                            .map((o) => o.replace(/IBC-/g, ''))
                                            .join(', ')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </CargoDetailsTable>
                </PanelSectionContent>
            </OdfjellPanel>
        )
    );
};

export default TankCargoDetailsPanel;
